import { API, Auth } from 'aws-amplify';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';

// ponytail: the maintenance config lives in ONE Post row with a fixed id, not a new
// SystemSetting @model. AI_RULES forbids unrequested schema changes, and a new @model would mean a
// CloudFormation update to the (fragile) prod API stack. Post rows are only ever read by id for
// learning content and are never listed, so this row is invisible to the rest of the app.
// Upgrade path: if a second global setting ever needs storing, add a real settings model and move
// this single row across.
export const MAINTENANCE_CONFIG_ID = 'system-maintenance-config';

export const DEFAULT_MAINTENANCE_CONFIG = {
  enabled: false,
  title: 'Scheduled maintenance',
  message:
    'LeanFITT™ Pro is temporarily unavailable while we carry out scheduled maintenance.\n\nPlease check back shortly — your data is safe.',
  // Whitelisted people keep full access while maintenance is on, so testing can continue.
  whitelist: ['dtapping99@gmail.com'],
  // Secret login gate. While maintenance is on, ONLY /login?key=<loginBypassKey> may reach the hub
  // hosted UI; plain /login shows the maintenance page (so no one else can sign in or migrate).
  // Empty string = no bypass link at all. This is separate from `whitelist`, which gates app ACCESS
  // after sign-in — this gates whether sign-in (and therefore migration) can even start.
  loginBypassKey: ''
};

/** Split a textarea / comma separated list into normalised email addresses. */
export const parseWhitelist = (raw) => {
  if (Array.isArray(raw)) return raw.map((e) => String(e).trim().toLowerCase()).filter(Boolean);
  return String(raw || '')
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
};

export const isWhitelisted = (email, whitelist) => {
  if (!email) return false;
  return parseWhitelist(whitelist).includes(String(email).trim().toLowerCase());
};

const normaliseConfig = (raw) => ({
  ...DEFAULT_MAINTENANCE_CONFIG,
  ...raw,
  enabled: Boolean(raw?.enabled),
  whitelist: parseWhitelist(raw?.whitelist ?? DEFAULT_MAINTENANCE_CONFIG.whitelist)
});

/**
 * Read the global maintenance config. Missing or unreadable row => defaults (maintenance OFF),
 * so a corrupted value can never lock the platform. API failures throw: callers decide, and the
 * gate deliberately fails open.
 */
export const fetchMaintenanceConfig = async () => {
  const response = await API.graphql({
    query: queries.getPost,
    variables: { id: MAINTENANCE_CONFIG_ID },
    authMode: 'API_KEY'
  });

  const content = response?.data?.getPost?.content;
  if (!content) return { ...DEFAULT_MAINTENANCE_CONFIG };

  try {
    return normaliseConfig(JSON.parse(content));
  } catch (error) {
    console.error('Maintenance config is not valid JSON, falling back to defaults:', error);
    return { ...DEFAULT_MAINTENANCE_CONFIG };
  }
};

/** Create or update the single maintenance config row. Returns the saved config. */
export const saveMaintenanceConfig = async (config) => {
  const next = normaliseConfig(config);
  const input = { id: MAINTENANCE_CONFIG_ID, content: JSON.stringify(next) };

  const existing = await API.graphql({
    query: queries.getPost,
    variables: { id: MAINTENANCE_CONFIG_ID },
    authMode: 'API_KEY'
  });

  await API.graphql({
    query: existing?.data?.getPost ? mutations.updatePost : mutations.createPost,
    variables: { input },
    authMode: 'API_KEY'
  });

  return next;
};

/** Email of the signed-in user, or null when nobody is signed in. */
export const getCurrentUserEmail = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return user?.attributes?.email || null;
  } catch (error) {
    return null;
  }
};
